import React, { useState, FormEvent } from 'react';
import { Issue } from '../models/Issue';
import { Box, Container, Typography, Button, Grid, CircularProgress, IconButton } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';

interface IssuePromptWithImageComponentProps {
  issue: Issue;
  onUpload: (file: File, text: string) => Promise<void>;
  onSkip: () => Promise<void>;
}

const IssuePromptWithImageComponent: React.FC<IssuePromptWithImageComponentProps> = ({ issue, onUpload, onSkip }) => {
  const { outstandingQuestion } = issue;
  const [file, setFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    onDrop: (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = () => {
        setThumbnail(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    noClick: true,
    noKeyboard: true
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) {
      setMessage('Please select a file to upload');
      return;
    }
    setLoading(true);
    await onUpload(file, outstandingQuestion.QuestionText);
    setLoading(false);
  };

  const handleNoPicture = () => {
    onSkip();
  };

  const handleRemoveFile = () => {
    setFile(null);
    setThumbnail(null);
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ mt: 4, width: '100%' }}>
        <Typography component="h1" variant="h5" gutterBottom>
          {outstandingQuestion.QuestionText}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          A picture is needed to answer this question.
        </Typography>
        <form onSubmit={handleSubmit} style={{ marginTop: '20px', width: '100%' }}>
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            {!thumbnail && (
              <Grid item xs={12}>
                <div {...getRootProps({ className: 'dropzone' })} style={{ border: '2px dashed #ccc', padding: '20px', textAlign: 'center' }}>
                  <input {...getInputProps()} />
                  <Typography>Drag 'n' drop an image here, or click to select a file</Typography>
                  <Button variant="contained" color="primary" onClick={open} sx={{ mt: 2 }}>
                    Select File
                  </Button>
                </div>
              </Grid>
            )}
            {thumbnail && (
              <Grid item xs={12} sx={{ position: 'relative' }}>
                <img src={thumbnail} alt="Selected file" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginTop: '20px' }} />
                <IconButton 
                  onClick={handleRemoveFile} 
                  sx={{ position: 'absolute', top: 10, right: 10, backgroundColor: 'rgba(255,255,255,0.8)' }}
                >
                  <CloseIcon />
                </IconButton>
                <Button variant="contained" color="primary" onClick={open} sx={{ mt: 2 }}>
                  Change File
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, py: 2, fontSize: '1.25rem' }}
                disabled={!file || loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Upload Picture'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                sx={{ mt: 3, py: 2, fontSize: '1.25rem' }}
                onClick={handleNoPicture}
              >
                I can't take a picture
              </Button>
            </Grid>
          </Grid>
          {message && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {message}
            </Typography>
          )}
        </form>
      </Box>
    </Container>
  );
};

export default IssuePromptWithImageComponent;
